import type {
  GenerationGeniusAPIResponse,
  IGeniusDiscussionQuestion,
  IGeniusNode,
  IGeniusVocabulary,
} from "@/types/models/GenerationGenius.interface";
import type { UnlevelledContentPDF } from "@/types/models/UnlevelledContent.interface";

/**
 * Converts a given time in seconds to a formatted string.
 *
 * @param seconds - The time in seconds to be formatted.
 * @returns A string representing the time in "HH:MM:SS" or "MM:SS" format.
 *          If the hours value is zero, the format will be "MM:SS".
 */
export function formatTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  const paddedHours = String(hrs).padStart(2, "0");
  const paddedMins = String(mins).padStart(2, "0");
  const paddedSecs = String(secs).padStart(2, "0");
  const hrsText = hrs > 0 ? `${paddedHours}:` : "";

  return `${hrsText}${paddedMins}:${paddedSecs}`;
}

/**
 * Formats the raw content data into a structured document for Generation Genius content.
 *
 * This function takes raw content data, including "what will you learn" and
 * "reading material" sections, and formats them into a structured document
 * compatible with the IGeniusNode type.
 *
 * @param {Object} data - The raw content data containing the sections to format.
 * @param {Object} data.raw_what_will_you_learn - Contains content for the "What will you learn" section.
 * @param {IGeniusNode[]} data.raw_what_will_you_learn.content - Content nodes for the "What will you learn" section.
 * @param {Object} data.raw_reading_materials - Contains content for the "Reading material" section.
 * @param {IGeniusNode[]} data.raw_reading_materials.content - Content nodes for the "Reading material" section.
 *
 * @return {IGeniusNode} - The formatted document node with all content sections.
 */

function getContentFromRawNode(
  rawNode: IGeniusNode | string | undefined,
): IGeniusNode[] {
  const node =
    typeof rawNode === "string"
      ? (JSON.parse(rawNode) as IGeniusNode)
      : rawNode;
  return node?.content || [];
}

/* Parses stringified JSON from the Generation Genius API response, converting string values into their corresponding JSON objects or arrays. */
export function parseContent(content: string | undefined): IGeniusNode[] | [] {
  let parsedContent;
  if (!content) return [];
  try {
    parsedContent = JSON.parse(content);
  } catch {
    parsedContent = [];
  }
  return parsedContent;
}

function makeDiscussionQuestionSection({
  label,
  items,
  hideContent = false,
}: {
  label?: string | undefined;
  items: IGeniusDiscussionQuestion[];
  hideContent?: boolean;
}) {
  return {
    type: "content_drawer_group",
    attrs: {
      label,
      drawers: items.map(({ question, answer }) => ({
        title: question,
        content: answer,
      })),
      hideContent,
    },
  };
}

/* Formats discussion questions into a structured document node that includes a heading for the current section i.e, before | after */
export function formatGeniusDiscussionQuestions(
  questions: string | undefined,
  hideContent = false,
): IGeniusNode {
  const parsed = (parseContent(questions) || []) as IGeniusDiscussionQuestion[];

  const beforeList = parsed.filter((q) => q?.section === "before");
  const afterList = parsed.filter((q) => q?.section === "after");
  const generalList = parsed.filter((q) => !q?.section);

  const sections: IGeniusNode[] = [];

  if (generalList.length) {
    sections.push(
      makeDiscussionQuestionSection({
        items: generalList,
        hideContent,
      }),
    );
  }

  if (beforeList.length) {
    sections.push(
      makeDiscussionQuestionSection({
        label: "Before the video",
        items: beforeList,
        hideContent,
      }),
    );
  }
  if (afterList.length) {
    sections.push(
      makeDiscussionQuestionSection({
        label: "After the video",
        items: afterList,
        hideContent,
      }),
    );
  }

  return { type: "doc", content: sections };
}

/* Formats vocabulary into a structured document node with content drawers for each word and its definition. */

export function formatGeniusVocabulary(
  vocabulary: string | undefined,
  hideContent = false,
): IGeniusNode {
  const parsedVocab = parseContent(
    vocabulary,
  ) as unknown as IGeniusVocabulary[];

  const drawers = parsedVocab
    .filter((vocab) => vocab.word && vocab.definition)
    .map(({ word, definition }) => ({
      title: word,
      content: definition,
    }));

  const sections: IGeniusNode[] = [];
  if (drawers.length) {
    sections.push({
      type: "content_drawer_group",
      attrs: {
        drawers,
        hideContent,
      },
    });
  }

  return {
    type: "doc",
    content: sections,
  };
}

export function formatPDFresources(
  pdfs: string | undefined,
): UnlevelledContentPDF[] {
  return parseContent(pdfs) as unknown as UnlevelledContentPDF[];
}

/* Formats the activity panel content, including vocabulary and discussion questions. */
export function formatGeniusActivityPanelContent(
  contentData: GenerationGeniusAPIResponse,
  hideContent?: boolean,
): {
  vocabulary: IGeniusNode;
  discussionQuestions: IGeniusNode;

  pdfResources: UnlevelledContentPDF[];
} {
  const vocabulary = formatGeniusVocabulary(
    contentData.vocabulary,
    hideContent,
  );
  const discussionQuestions = formatGeniusDiscussionQuestions(
    contentData.discussion_questions,
    hideContent,
  );

  const pdfResources = formatPDFresources(contentData?.pdfs);

  return {
    vocabulary,
    discussionQuestions,
    pdfResources,
  };
}

/* Formats the raw text content into a structured document node for Generation Genius content. */
export function formatGeniusContentDocument({
  raw_text: rawText,
}: GenerationGeniusAPIResponse) {
  const whatWillYouLearn = getContentFromRawNode(rawText);

  const finalDoc: IGeniusNode = {
    type: "doc",
    content: whatWillYouLearn,
  };

  return finalDoc;
}
